/* eslint-disable max-statements */
import { Grid, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useMemo, useState } from 'react';

import {
  Declaration,
  DeclarationDocumentsAdditionalFiscalReferenceRole,
  DeclarationHighValueGroupType,
  DeclarationStatus,
  EnvironmentCountries,
} from '@e-origin/shared';

import { failedIcon, notSentIcon, releasedIcon } from '@assets';
import { EucdmCode, HideElementFor, InputImage, MultiLangCodes, TextInput } from '@components';
import { useCheckImporterEori, useCheckIntracomVat } from '@hooks/declaration';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField, isHighValueDeclaration } from '@utils';
import { BuyerForm } from '../BuyerForm';
import { DeclarationStakeholdersFormValues } from './validateDeclarationStakeholdersForm';

interface StakeholderImporterSectionProps {
  form: UseFormReturnType<DeclarationStakeholdersFormValues>;
  editDisabled?: boolean;
  declaration: Declaration;
  isHighValue: boolean;
  declarationGroup: string;
}

export const StakeholderImporterSection: React.FC<StakeholderImporterSectionProps> = ({ form, editDisabled, declaration, isHighValue, declarationGroup }) => {
  const { data: settings } = useSettings();
  const checkImporterEoriMutation = useCheckImporterEori();
  const checkIntracomVatMutation = useCheckIntracomVat();
  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport: declaration?.generalInfo?.isExport,
  });

  const canVerifyDeclaration = useMemo(
    () => [DeclarationStatus.ANALYSIS, DeclarationStatus.NOT_SENT, DeclarationStatus.REJECTED].includes(declaration.generalInfo.status),
    [declaration.generalInfo.status],
  );

  const verifyImporterEori = async () => {
    if (!canVerifyDeclaration) return;

    checkImporterEoriMutation.mutate({
      _id: declaration._id,
      lrn: declaration.messageInfo.LRN,
      eori: form.values.stakeholders.importer.identificationNumber,
    });
  };

  const verifyIntracomVat = async () => {
    if (!canVerifyDeclaration) return;

    checkIntracomVatMutation.mutate({
      _id: declaration._id,
      lrn: declaration.messageInfo.LRN,
      eori: form.values.stakeholders.intracom.identificationNumber,
    });
  };

  const [importerEoriStatus, setImporterEoriStatus] = useState({ value: undefined, icon: notSentIcon }),
    [intracomVatStatus, setIntracomVatStatus] = useState({ value: undefined, icon: notSentIcon });

  const customerVatIdentificationNumber = declaration.documents?.additionalFiscalReference.find(
    (ref) => ref.role === DeclarationDocumentsAdditionalFiscalReferenceRole.CUSTOMER,
  )?.vatIdentificationNumber;

  useEffect(() => {
    if (declaration.stakeholders.importer.evalEORI?.eoriOk === undefined) {
      setImporterEoriStatus({ value: undefined, icon: notSentIcon });
    } else if (declaration.stakeholders.importer.evalEORI?.eoriOk) {
      setImporterEoriStatus({ value: true, icon: releasedIcon });
    } else {
      setImporterEoriStatus({ value: false, icon: failedIcon });
    }

    if (declaration.stakeholders.intracom?.evalVAT?.vatOk === undefined) {
      setIntracomVatStatus({ value: undefined, icon: notSentIcon });
    } else if (declaration.stakeholders.intracom?.evalVAT?.vatOk) {
      setIntracomVatStatus({ value: true, icon: releasedIcon });
    } else {
      setIntracomVatStatus({ value: false, icon: failedIcon });
    }
  }, [declaration]);

  return (
    <Stack>
      <Stack mb="xl">
        <Title order={5} fw={600}>
          Importer
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="stakeholders.importer.name">
              <TextInput
                {...form.getInputProps('stakeholders.importer.name')}
                label="Name"
                value={form.values.stakeholders.importer.name}
                disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={6}>
            <EucdmCode path="stakeholders.importer.identificationNumber">
              <TextInput
                {...form.getInputProps('stakeholders.importer.identificationNumber')}
                label="Identification number"
                value={form.values.stakeholders.importer.identificationNumber}
                rightSection={
                  isHighValueDeclaration(declaration.generalInfo.group) ? (
                    <InputImage picture={importerEoriStatus.icon} onPictureClick={verifyImporterEori} disabled={!canVerifyDeclaration} />
                  ) : null
                }
                disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.importer.address.street">
              <TextInput
                {...form.getInputProps('stakeholders.importer.address.street')}
                label="Street and number"
                disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.importer.address.postcode">
              <TextInput
                {...form.getInputProps('stakeholders.importer.address.postcode')}
                label="Postcode"
                disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.importer.address.city">
              <TextInput
                {...form.getInputProps('stakeholders.importer.address.city')}
                label="City"
                disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.importer.address.country">
              <MultiLangCodes
                {...form.getInputProps('stakeholders.importer.address.country')}
                label="Country"
                code="CL199"
                disabled={editDisabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
        {isHighValue && settings.country === EnvironmentCountries.NL && (
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('stakeholders.intracom.identificationNumber')}
                label="VAT Identification number (For information only, not declared)"
                value={form.values.stakeholders.intracom.identificationNumber || customerVatIdentificationNumber || ''}
                rightSection={<InputImage picture={intracomVatStatus.icon} onPictureClick={verifyIntracomVat} disabled={!canVerifyDeclaration} />}
                disabled={editDisabled || (customerVatIdentificationNumber && !form.values.stakeholders.intracom.identificationNumber)}
              />
            </Grid.Col>
          </Grid>
        )}
      </Stack>

      {isHighValue && !(declarationGroup === DeclarationHighValueGroupType.H2) && (
        <BuyerForm form={form} editDisabled={editDisabled} declarationGroup={declarationGroup} isExport={declaration?.generalInfo?.isExport} />
      )}

      <HideElementFor
        systems={['DMS', 'DECO', 'IDMS_LV']}
        declarationGroup={declarationGroup}
        condition={() => declarationGroup === DeclarationHighValueGroupType.H2}
      >
        <Stack mb="xl">
          <Title order={5} fw={600}>
            Intracom
          </Title>

          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('stakeholders.intracom.name')} label="Name" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('stakeholders.intracom.identificationNumber')}
                label="Identification number"
                value={form.values.stakeholders.intracom.identificationNumber || customerVatIdentificationNumber || ''}
                rightSection={
                  isHighValueDeclaration(declaration.generalInfo.group) ? (
                    <InputImage picture={intracomVatStatus.icon} onPictureClick={verifyIntracomVat} disabled={!canVerifyDeclaration} />
                  ) : null
                }
                disabled={editDisabled || (customerVatIdentificationNumber && !form.values.stakeholders.intracom.identificationNumber)}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('stakeholders.intracom.address.street')} label="Street and number" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('stakeholders.intracom.address.postcode')} label="Postcode" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('stakeholders.intracom.address.city')} label="City" disabled={editDisabled} />
            </Grid.Col>

            <Grid.Col span={3}>
              <MultiLangCodes
                {...form.getInputProps('stakeholders.intracom.address.country')}
                label="Country"
                code="CL199"
                disabled={editDisabled}
                system={codelistSystems}
              />
            </Grid.Col>
          </Grid>
        </Stack>
      </HideElementFor>
    </Stack>
  );
};
